import React, { useEffect, useState } from 'react';
import { Phone, Instagram, ArrowRight, Menu, X, Camera, Building2, ChevronRight, Trophy, MapPin, Mail } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Avrasya Sportif Faaliyetler — Public Landing Page
// Design: Swiss/High-Contrast + Performance Pro
// Brand: #0052A5 (deep Avrasya blue) + black + accent red #FF3B30
// Fonts: Outfit (heading) + Manrope (body)

const HERO_BG = 'https://static.prod-images.emergentagent.com/jobs/0d7d9b01-05a3-40c5-aa85-6f66012d1d9c/images/1ac534eea2e6d1fe6433929dcc21747da996860e0509fe2ae4ebffd6eabbfe5c.png';
const IMG_CORPORATE = 'https://images.unsplash.com/photo-1774599661175-df253e46182a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400';
const IMG_BROADCAST = 'https://images.unsplash.com/photo-1646186017677-269887e86898?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400';
const IMG_VOLLEY1 = 'https://images.unsplash.com/photo-1732446572692-832ba3d85ac8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200';
const IMG_VOLLEY2 = 'https://images.unsplash.com/photo-1762093805066-ca2afb453151?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200';
const IMG_SCHOOL = 'https://images.unsplash.com/photo-1521412644187-c49fa049e84d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200';
const IMG_HOLDING = 'https://images.unsplash.com/photo-1517649763962-0c623066013b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200';

const ORGANIZATIONS = [
  { id: 'avrasya', slug: 'avrasya-voleybol-turnuvasi', title: 'Avrasya Voleybol Turnuvası', sub: 'Geleneksel turnuva organizasyonu', img: IMG_VOLLEY1, year: '2024 — 2026' },
  { id: 'dogan', slug: 'dogan-holding-spor-senlikleri', title: 'Doğan Holding Spor Şenlikleri', sub: 'Kurumsal şirket içi etkinlik', img: IMG_HOLDING, year: '2025' },
  { id: 'amator', slug: 'amator-voleybol-organizasyonu', title: 'Amatör Voleybol Organizasyonu', sub: 'Şehirler arası lig', img: IMG_VOLLEY2, year: '2025 & 2026' },
  { id: 'rumeli', slug: 'rumeli-universitesi-spor-ve-sanat-yaz-okulu', title: 'Rumeli Üniversitesi Spor ve Sanat Yaz Okulu', sub: 'Üniversite spor programı', img: IMG_SCHOOL, year: '2025' },
];

const NAV = [
  { href: '#hizmetler', label: 'Hizmetler' },
  { href: '#organizasyonlar', label: 'Organizasyonlar' },
  { href: '/blog', label: 'Blog' },
  { href: '#hakkimizda', label: 'Hakkımızda' },
  { href: '#iletisim', label: 'İletişim' },
];

const LandingPage = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [orgsFromDb, setOrgsFromDb] = useState([]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    axios.get(`${API}/events`).then(r => {
      const list = (r.data || []).map(e => ({
        id: e.id,
        slug: e.slug || e.id,
        title: e.title,
        sub: e.subtitle || e.description || '',
        img: e.image_url,
        year: e.year || e.date || '',
        from_db: true,
      }));
      setOrgsFromDb(list);
    }).catch(() => setOrgsFromDb([]));
  }, []);

  // DB'de etkinlik varsa onları, yoksa varsayılan örnekleri göster
  const displayOrgs = orgsFromDb.length > 0 ? orgsFromDb : ORGANIZATIONS;

  return (
    <div
      className="min-h-screen bg-white text-slate-900"
      style={{
        fontFamily: '"Manrope", system-ui, sans-serif',
        scrollBehavior: 'smooth'
      }}
      data-testid="public-landing-page"
    >
      {/* Google Fonts */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
      <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@500;700;800;900&family=Manrope:wght@300;400;500;600;700&display=swap" rel="stylesheet" />

      {/* ============ NAV ============ */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
        }`}
        data-testid="public-nav"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16">
          <div className="flex items-center justify-between h-20">
            <a href="#hero" className="flex items-center gap-3 group">
              <img src="/avrasya-logo.jpg" alt="Avrasya Sportif" className="h-12 w-12 object-contain rounded" />
              <div className={`leading-tight ${scrolled ? 'text-slate-900' : 'text-white'} transition-colors`}>
                <p className="text-base font-black tracking-tight" style={{ fontFamily: 'Outfit' }}>AVRASYA</p>
                <p className="text-[10px] tracking-[0.25em] -mt-0.5 opacity-75">SPORTİF FAALİYETLER</p>
              </div>
            </a>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              {NAV.map(item => (
                <a
                  key={item.href}
                  href={item.href}
                  className={`text-sm font-medium tracking-wide hover:text-[#0052A5] transition-colors ${
                    scrolled ? 'text-slate-700' : 'text-white/90'
                  }`}
                  data-testid={`nav-link-${item.href.slice(1)}`}
                >
                  {item.label}
                </a>
              ))}
              <a
                href="#iletisim"
                className="bg-[#0052A5] hover:bg-[#003D7A] text-white px-5 py-2.5 text-sm font-semibold tracking-wide transition-all hover:scale-[1.02] active:scale-95"
                style={{ fontFamily: 'Outfit' }}
                data-testid="nav-cta-contact"
              >
                BİZE ULAŞIN
              </a>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className={`md:hidden ${scrolled ? 'text-slate-900' : 'text-white'}`}
              data-testid="mobile-menu-toggle"
            >
              {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="md:hidden bg-white border-t border-slate-200 shadow-lg" data-testid="mobile-menu">
            <div className="px-6 py-4 space-y-3">
              {NAV.map(item => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={() => setMenuOpen(false)}
                  className="block py-2 text-slate-700 hover:text-[#0052A5] font-medium"
                >
                  {item.label}
                </a>
              ))}
              <a href="#iletisim" onClick={() => setMenuOpen(false)} className="block w-full bg-[#0052A5] text-white text-center py-3 font-semibold mt-2">
                BİZE ULAŞIN
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* ============ HERO ============ */}
      <section
        id="hero"
        className="relative min-h-[100vh] flex items-end overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(0,30,60,0.85) 0%, rgba(0,82,165,0.7) 100%), url(${HERO_BG})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
        data-testid="hero-section"
      >
        {/* Decorative top-right accent */}
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-bl from-[#0052A5]/40 via-transparent to-transparent pointer-events-none"></div>
        <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-[#FF3B30]/10 blur-3xl pointer-events-none"></div>

        <div className="relative max-w-7xl mx-auto px-6 sm:px-12 lg:px-16 w-full pb-20 sm:pb-32 pt-32">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 px-4 py-1.5 mb-6">
              <span className="w-1.5 h-1.5 bg-[#FF3B30] rounded-full animate-pulse"></span>
              <span className="text-[11px] font-semibold tracking-[0.2em] text-white/90">SPORTİF • KURUMSAL • PROFESYONEL</span>
            </div>
            <h1
              className="text-white text-5xl sm:text-6xl lg:text-7xl tracking-tighter font-black uppercase leading-[0.95] mb-6"
              style={{ fontFamily: 'Outfit' }}
              data-testid="hero-heading"
            >
              Kurumsal &<br />
              Sportif<br />
              <span className="text-[#FF3B30]">Organizasyonların</span><br />
              Gücü
            </h1>
            <p className="text-white/80 text-lg sm:text-xl max-w-2xl mb-10 leading-relaxed">
              Şirket içi etkinliklerden profesyonel canlı yayın çözümlerine, voleybol turnuvalarından üniversite yaz okullarına — sporun her alanında 360° organizasyon hizmeti.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <a
                href="#hizmetler"
                className="group inline-flex items-center gap-2 bg-[#0052A5] hover:bg-white hover:text-[#0052A5] text-white px-8 py-4 text-sm font-bold tracking-wider transition-all"
                style={{ fontFamily: 'Outfit' }}
                data-testid="hero-cta-primary"
              >
                HİZMETLERİMİZ
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </a>
              <a
                href="#iletisim"
                className="inline-flex items-center gap-2 border-2 border-white/40 hover:border-white text-white px-8 py-4 text-sm font-bold tracking-wider transition-all hover:bg-white/10"
                style={{ fontFamily: 'Outfit' }}
                data-testid="hero-cta-secondary"
              >
                BİZE ULAŞIN
              </a>
            </div>
          </div>

          {/* Stats strip */}
          <div className="absolute bottom-0 left-0 right-0 bg-black/40 backdrop-blur-md border-t border-white/10">
            <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16 py-5">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8 text-white">
                <div><p className="text-3xl font-black" style={{ fontFamily: 'Outfit' }}>50+</p><p className="text-[11px] tracking-widest uppercase opacity-70">Organizasyon</p></div>
                <div><p className="text-3xl font-black" style={{ fontFamily: 'Outfit' }}>15K+</p><p className="text-[11px] tracking-widest uppercase opacity-70">Katılımcı</p></div>
                <div><p className="text-3xl font-black" style={{ fontFamily: 'Outfit' }}>2-3</p><p className="text-[11px] tracking-widest uppercase opacity-70">Kameralı Yayın</p></div>
                <div><p className="text-3xl font-black" style={{ fontFamily: 'Outfit' }}>360°</p><p className="text-[11px] tracking-widest uppercase opacity-70">Çözüm</p></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ SERVICES ============ */}
      <section
        id="hizmetler"
        className="py-24 sm:py-32 bg-[#F4F5F7]"
        data-testid="services-section"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
            <div>
              <p className="text-[#0052A5] font-bold text-sm tracking-[0.3em] mb-3">— HİZMETLERİMİZ</p>
              <h2 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-black uppercase leading-[0.95] text-slate-900" style={{ fontFamily: 'Outfit' }}>
                Sahanın<br />Profesyonelleri
              </h2>
            </div>
            <p className="md:max-w-md text-slate-600 leading-relaxed">
              Avrasya Sportif olarak iki ana hizmet alanında uzmanlaşmış, deneyimli bir ekiple sportif organizasyonlarınızı baştan sona yönetiyoruz.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-8">
            {/* Card 1 - Corporate Events (col-span-7) */}
            <a href="#iletisim" className="md:col-span-7 group relative h-[480px] overflow-hidden bg-slate-900 cursor-pointer" data-testid="service-card-corporate">
              <img src={IMG_CORPORATE} alt="Kurumsal Etkinlik" className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/50 to-transparent"></div>
              <div className="absolute top-6 left-6 w-12 h-12 bg-[#0052A5] flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-8 lg:p-10 text-white">
                <p className="text-xs tracking-[0.3em] font-semibold opacity-70 mb-3">01 — KURUMSAL</p>
                <h3 className="text-3xl lg:text-4xl font-black uppercase tracking-tight mb-3 leading-[0.95]" style={{ fontFamily: 'Outfit' }}>
                  Şirket İçi Kurumsal<br />Etkinlik Organizasyonları
                </h3>
                <p className="text-white/80 text-sm leading-relaxed mb-4 max-w-xl">
                  Şirketlerinin takım ruhunu güçlendirin. Spor şenlikleri, takım yarışmaları, kurumsal turnuvalar ve özel etkinlikler — başından sonuna profesyonel organizasyon.
                </p>
                <div className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider group-hover:gap-3 transition-all">
                  Detaylar <ChevronRight className="w-4 h-4" />
                </div>
              </div>
            </a>

            {/* Card 2 - Live Broadcast (col-span-5) */}
            <a href="#iletisim" className="md:col-span-5 group relative h-[480px] overflow-hidden bg-slate-900 cursor-pointer" data-testid="service-card-broadcast">
              <img src={IMG_BROADCAST} alt="Canlı Yayın" className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0052A5] via-[#0052A5]/40 to-transparent"></div>
              <div className="absolute top-6 left-6 w-12 h-12 bg-[#FF3B30] flex items-center justify-center">
                <Camera className="w-6 h-6 text-white" />
              </div>
              <div className="absolute top-6 right-6 inline-flex items-center gap-1.5 bg-[#FF3B30] text-white px-2.5 py-1 text-[10px] font-bold tracking-widest">
                <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span> CANLI
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-8 lg:p-10 text-white">
                <p className="text-xs tracking-[0.3em] font-semibold opacity-80 mb-3">02 — YAYIN</p>
                <h3 className="text-3xl lg:text-4xl font-black uppercase tracking-tight mb-3 leading-[0.95]" style={{ fontFamily: 'Outfit' }}>
                  Canlı Yayın<br />Çözümleri
                </h3>
                <p className="text-white/90 text-sm leading-relaxed mb-4">
                  Profesyonel <strong>2-3 kameralı sistem</strong> ile maçlarınızı, etkinliklerinizi ve turnuvalarınızı kesintisiz canlı yayınlayın. Stüdyo kalitesinde görüntü.
                </p>
                <div className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider group-hover:gap-3 transition-all">
                  Detaylar <ChevronRight className="w-4 h-4" />
                </div>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* ============ ORGANIZATIONS ============ */}
      <section
        id="organizasyonlar"
        className="py-24 sm:py-32 bg-[#0A0A0A] text-white"
        data-testid="organizations-section"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
            <div>
              <p className="text-[#FF3B30] font-bold text-sm tracking-[0.3em] mb-3">— REFERANSLAR</p>
              <h2 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-black uppercase leading-[0.95]" style={{ fontFamily: 'Outfit' }}>
                Geçmiş<br />Organizasyonlar
              </h2>
            </div>
            <p className="md:max-w-md text-white/60 leading-relaxed">
              Yılların deneyimi ile gerçekleştirdiğimiz organizasyonların bir kısmı.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-5">
            {displayOrgs.map((org, idx) => (
              <a
                key={org.id || org.slug}
                href={`/organizasyon/${org.slug || org.id}`}
                className="group relative aspect-[4/5] overflow-hidden border border-white/10 hover:border-[#0052A5] transition-all"
                data-testid={`org-card-${org.slug || org.id}`}
              >
                {org.img ? (
                  <img src={org.img} alt={org.title} className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-80 group-hover:scale-105 transition-all duration-700" />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-[#0052A5]/30 to-black" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
                <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 text-white/60">
                  <span className="text-[10px] font-mono tracking-widest">{String(idx + 1).padStart(2, '0')}</span>
                  <span className="w-5 h-px bg-white/30"></span>
                  <Trophy className="w-3 h-3" />
                </div>
                {org.year && (
                  <div className="absolute top-3 right-3 inline-flex items-center bg-white/10 backdrop-blur px-2 py-0.5 text-[9px] font-bold tracking-widest text-white/80">
                    {org.year}
                  </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <h3 className="text-sm md:text-base font-black uppercase tracking-tight leading-tight mb-1 line-clamp-2" style={{ fontFamily: 'Outfit' }}>
                    {org.title}
                  </h3>
                  {org.sub && (
                    <p className="text-white/60 text-[10px] tracking-wider uppercase line-clamp-1">{org.sub}</p>
                  )}
                  <div className="mt-2 inline-flex items-center gap-1 text-[10px] text-white/40 group-hover:text-[#FF3B30] transition-colors">
                    Detayları Gör <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ============ ABOUT ============ */}
      <section id="hakkimizda" className="py-24 sm:py-32 bg-white" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            <div className="lg:col-span-5">
              <p className="text-[#0052A5] font-bold text-sm tracking-[0.3em] mb-3">— HAKKIMIZDA</p>
              <h2 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-black uppercase leading-[0.95] text-slate-900 mb-8" style={{ fontFamily: 'Outfit' }}>
                Sporda<br />Profesyonel<br /><span className="text-[#0052A5]">Çözüm Ortağı</span>
              </h2>
              <img src="/avrasya-logo.jpg" alt="Avrasya Sportif" className="w-44 h-44 object-contain" />
            </div>
            <div className="lg:col-span-7 space-y-6 text-slate-700 leading-relaxed">
              <p className="text-lg sm:text-xl text-slate-900 font-medium leading-snug">
                <strong>Avrasya Sportif Faaliyetler</strong>; kurumsal profesyonelliği sportif enerjiyle buluşturan, alanında öncü organizasyon şirketidir.
              </p>
              <p>
                Şirket içi takım ruhunu güçlendirmek isteyen markalardan, üniversite spor okullarına; amatör voleybol turnuvalarından profesyonel canlı yayın yapımlarına kadar geniş bir yelpazede hizmet veriyoruz. Her organizasyonu, müşterimizin vizyonunu sahaya yansıtan bir deneyim olarak ele alıyoruz.
              </p>
              <p>
                Deneyimli ekibimiz, modern <strong>2-3 kameralı canlı yayın sistemimiz</strong> ve titiz operasyonel altyapımızla — küçük şirket içi etkinliklerden büyük şehirler arası organizasyonlara kadar her ölçekte çözüm sunuyoruz.
              </p>

              <div className="grid grid-cols-2 gap-6 pt-6 border-t border-slate-200">
                <div>
                  <p className="text-xs tracking-[0.25em] uppercase text-slate-500 mb-2">Vizyon</p>
                  <p className="font-semibold text-slate-900">Türkiye'nin lider sportif organizasyon markası olmak</p>
                </div>
                <div>
                  <p className="text-xs tracking-[0.25em] uppercase text-slate-500 mb-2">Misyon</p>
                  <p className="font-semibold text-slate-900">Kusursuz organizasyon ile sporu yaşatmak</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ CONTACT ============ */}
      <section id="iletisim" className="py-24 sm:py-32 bg-[#0052A5] text-white relative overflow-hidden" data-testid="contact-section">
        <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-white/5 blur-3xl"></div>
        <div className="absolute -bottom-20 -left-20 w-96 h-96 rounded-full bg-[#FF3B30]/10 blur-3xl"></div>

        <div className="relative max-w-7xl mx-auto px-6 sm:px-12 lg:px-16">
          <p className="text-white/60 font-bold text-sm tracking-[0.3em] mb-3">— İLETİŞİM</p>
          <h2 className="text-4xl sm:text-5xl lg:text-7xl tracking-tighter font-black uppercase leading-[0.9] mb-12" style={{ fontFamily: 'Outfit' }}>
            Bir sonraki<br />organizasyonunuz<br />için <span className="italic font-light">hazırız.</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Phone */}
            <a
              href="tel:+905053686091"
              className="group p-8 lg:p-10 bg-white/5 backdrop-blur border border-white/10 hover:bg-white hover:text-[#0052A5] transition-all"
              data-testid="contact-phone-link"
            >
              <Phone className="w-8 h-8 mb-4 opacity-70 group-hover:opacity-100" />
              <p className="text-xs tracking-[0.3em] uppercase opacity-60 mb-2">Telefon</p>
              <p className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight transition-transform group-hover:translate-x-2" style={{ fontFamily: 'Outfit' }}>
                0505 368 60 91
              </p>
              <p className="mt-3 text-sm opacity-70 group-hover:opacity-100">Hafta içi 09:00 — 18:00</p>
            </a>

            {/* Instagram */}
            <a
              href="https://instagram.com/avrasyasportif"
              target="_blank"
              rel="noopener noreferrer"
              className="group p-8 lg:p-10 bg-white/5 backdrop-blur border border-white/10 hover:bg-white hover:text-[#0052A5] transition-all"
              data-testid="contact-instagram-link"
            >
              <Instagram className="w-8 h-8 mb-4 opacity-70 group-hover:opacity-100" />
              <p className="text-xs tracking-[0.3em] uppercase opacity-60 mb-2">Instagram</p>
              <p className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight transition-transform group-hover:translate-x-2" style={{ fontFamily: 'Outfit' }}>
                @avrasyasportif
              </p>
              <p className="mt-3 text-sm opacity-70 group-hover:opacity-100">Etkinliklerimizi takip edin</p>
            </a>
          </div>

          {/* E-posta - tam genişlik */}
          <a
            href="mailto:info@avrasyasportif.com"
            className="group mt-6 flex items-center gap-5 p-8 lg:p-10 bg-white/5 backdrop-blur border border-white/10 hover:bg-white hover:text-[#0052A5] transition-all"
            data-testid="contact-email-link"
          >
            <Mail className="w-8 h-8 opacity-70 group-hover:opacity-100 shrink-0" />
            <div className="min-w-0">
              <p className="text-xs tracking-[0.3em] uppercase opacity-60 mb-2">E-posta</p>
              <p className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight transition-transform group-hover:translate-x-2 break-all" style={{ fontFamily: 'Outfit' }}>
                info@avrasyasportif.com
              </p>
              <p className="mt-2 text-sm opacity-70 group-hover:opacity-100">Teklif ve sorularınızı buraya iletebilirsiniz</p>
            </div>
          </a>

          <div className="mt-12 inline-flex items-center gap-3 text-white/70 text-sm">
            <MapPin className="w-4 h-4" />
            <span>İstanbul, Türkiye • Tüm Türkiye'ye hizmet</span>
          </div>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="bg-black text-white relative overflow-hidden" data-testid="footer-section">
        {/* Watermark */}
        <p
          className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-[20vw] font-black tracking-tighter text-white/[0.03] pointer-events-none select-none whitespace-nowrap"
          style={{ fontFamily: 'Outfit' }}
        >
          AVRASYA
        </p>

        <div className="relative max-w-7xl mx-auto px-6 sm:px-12 lg:px-16 py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-12">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <img src="/avrasya-logo.jpg" alt="Avrasya Sportif" className="h-12 w-12 object-contain rounded" />
                <div className="leading-tight">
                  <p className="text-base font-black tracking-tight" style={{ fontFamily: 'Outfit' }}>AVRASYA</p>
                  <p className="text-[10px] tracking-[0.25em] -mt-0.5 opacity-50">SPORTİF FAALİYETLER</p>
                </div>
              </div>
              <p className="text-white/50 text-sm max-w-md leading-relaxed">
                Kurumsal etkinlikler, voleybol turnuvaları ve profesyonel canlı yayın çözümlerinde Türkiye'nin güvenilir sportif organizasyon markası.
              </p>
            </div>
            <div>
              <p className="text-xs tracking-[0.25em] uppercase text-white/40 mb-3">Menü</p>
              <ul className="space-y-2 text-sm">
                {NAV.map(item => (
                  <li key={item.href}><a href={item.href} className="text-white/70 hover:text-white">{item.label}</a></li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-xs tracking-[0.25em] uppercase text-white/40 mb-3">İletişim</p>
              <ul className="space-y-2 text-sm">
                <li><a href="tel:+905053686091" className="text-white/70 hover:text-white inline-flex items-center gap-2"><Phone className="w-3.5 h-3.5" /> 0505 368 60 91</a></li>
                <li><a href="mailto:info@avrasyasportif.com" className="text-white/70 hover:text-white inline-flex items-center gap-2"><Mail className="w-3.5 h-3.5" /> info@avrasyasportif.com</a></li>
                <li><a href="https://instagram.com/avrasyasportif" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-white inline-flex items-center gap-2"><Instagram className="w-3.5 h-3.5" /> @avrasyasportif</a></li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <p className="text-xs text-white/40">© {new Date().getFullYear()} Avrasya Sportif Faaliyetler. Tüm hakları saklıdır.</p>
            <p className="text-[10px] text-white/30 tracking-wider">Şirket İçi Kurumsal Etkinlik · Canlı Yayın Çözümleri · Voleybol Turnuvası Organizasyonu</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
